import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Auth from "../../Auth";
import { listBuiltinCharacterTemplates, stripCharacterTemplatePrefix, getCharacterTemplateEntityType, formatCharacterRoleLabel } from "../../../../src/shared/characterTemplates.js";
import { BLANK_TIPTAP_JSON } from "../../../../src/shared/schemas/CharacterSchema.js";

const BUILTIN_TEMPLATES = listBuiltinCharacterTemplates();
const BUILTIN_META_MAP = BUILTIN_TEMPLATES.reduce((acc, template) => {
  acc.set(template.optionValue, template);
  return acc;
}, new Map());

const isValidTipTapDoc = (value) =>
  value && typeof value === "object" && value.type === "doc" && Array.isArray(value.content);

const cloneFields = (fields = []) =>
  fields.map((field) => ({
    ...field,
    value: isValidTipTapDoc(field.value) ? { ...field.value } : BLANK_TIPTAP_JSON,
  }));

export const useCharacterTemplates = () => {
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Auth.fetchWithIdToken("/api/entity-default-templates/list", { entityTypePrefix: "character:" });
      setCustomTemplates(result.defaultTemplates || []);
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "character_templates" } });
      setCustomTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const mergedTemplates = useMemo(() => {
    const templateMap = new Map();
    BUILTIN_TEMPLATES.forEach((template) => {
      templateMap.set(template.optionValue, {
        id: null,
        role: template.optionValue,
        label: template.name,
        description: template.description,
        defaultFields: cloneFields(template.defaultFields),
        entityType: getCharacterTemplateEntityType(template.optionValue),
        source: "builtin",
      });
    });

    customTemplates.forEach((template) => {
      const role = stripCharacterTemplatePrefix(template.entityType);
      const meta = BUILTIN_META_MAP.get(role);
      templateMap.set(role, {
        id: template.id,
        role,
        label: meta?.name || formatCharacterRoleLabel(role),
        description: meta?.description,
        defaultFields: cloneFields(template.defaultFields),
        entityType: template.entityType,
        source: meta ? "customOverride" : "custom",
      });
    });

    return Array.from(templateMap.values());
  }, [customTemplates]);

  const roleOptions = useMemo(
    () =>
      mergedTemplates.map((template) => ({
        value: template.role,
        label: template.label,
        description: template.description,
      })),
    [mergedTemplates]
  );

  const getTemplateForRole = useCallback(
    (role) => {
      const normalizedRole = role || mergedTemplates[0]?.role;
      return mergedTemplates.find((template) => template.role === normalizedRole) || mergedTemplates[0];
    },
    [mergedTemplates]
  );

  return {
    templates: mergedTemplates,
    roleOptions,
    loading,
    refreshTemplates: loadTemplates,
    getTemplateForRole,
    builtinTemplates: BUILTIN_TEMPLATES,
  };
};
